import { create } from 'axios';

import {
  AddEditFilter,
  ChartSort,
  ChartsSortDirection,
  Connector,
  ConnectorForEvent,
  ConnectorSettings,
  CustomizedEditedEventData,
  CustomizedEvent,
  CustomizedEventBody,
  CustomizedEventData,
  DashboardDataResponse,
  Deployment,
  DeploymentInfoResponse,
  DismissEvent,
  DismissEventSetting,
  Event,
  GetCloudConnectedResponse,
  GetConfigResponse,
  GetElasticCloudApiKeyResponse,
  GetIndexMetricsPayload,
  GetIndexMetricsResponse,
  GetIndicesPayload,
  GetIndicesResponse,
  GetNotificationsHistoryPayload,
  GetNotificationsSummaryPayload,
  GetProjectMetricsListResponse,
  GetTokenResponse,
  Level,
  ListFilter,
  MetricListTypes,
  MetricsListResponse,
  MetricsPayload,
  MetricsResponse,
  NotificationsHistorySummaryResponse,
  NotificationsHistoryTableResponse,
  Project,
  SearchTierMetricType,
  SearchTierSettingsResponse,
  TemplateResultResponse,
  TemplatesHistoryResponse,
  GetNodesResponse,
  GetNodesMetricsResponse,
  DeploymentTierPerformanceTier,
  DeploymentTierPerformanceResponse,
  DeploymentAnalysisResponse,
  GetNodesMetricsPayload,
  PermissionsResponse,
  ShardsViewBody,
  IndexNodesRatesResponse,
  IndexOnNodeDataBody,
  IndexOnNodeDataResponse,
  DashboardEventsResponse,
  ResourceType,
  GetEventsOverTimePathParams,
  GetEventsOverTimePayload,
  GetEventsOverTimeResponse,
  GetOpenEventGroupsPathParams,
  GetOpenEventGroupsPayload,
  GetOpenEventGroupsResponse,
  GetClosedEventGroupsPathParams,
  GetClosedEventGroupsPayload,
  GetClosedEventGroupsResponse,
  GetOpenEventsPathParams,
  GetOpenEventsPayload,
  GetOpenEventsResponse,
  GetClosedEventsPathParams,
  GetClosedEventsPayload,
  GetClosedEventsResponse,
} from './types';

import type { EventSeverity } from '@/apps/autoops/modules/utils/event-severity';

type PropsWithSignal<T extends object = object> = T & {
  signal?: AbortSignal;
};

export const instance = create();

export const getConfig = () => {
  const baseUrl = '/assets/configs/app.config.json';
  const url = import.meta.env.DEV ? `/src${baseUrl}` : baseUrl;

  return instance.get<GetConfigResponse>(url);
};

export const getCloudConnectedStatus = (
  organizationId: string,
  address: string,
  region: string
) => {
  return instance.post<GetCloudConnectedResponse>(
    `/api/installation-wizard/v1/organization/${organizationId}/status?region=${region}`,
    { address },
    {
      headers: {
        organizationId,
        // Adding header to avoid showing a toast error
        skipAutoOpsError: 'true',
      },
      // 404 is an accepted value
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status == 404,
    }
  );
};

export const getToken = (region: string) => {
  return instance.post<GetTokenResponse>(
    `/api/signup/register/organization?region=${region}`,
    undefined, // no payload
    {
      // Adding header to avoid showing a toast error
      headers: { skipAutoOpsError: 'true' },
    }
  );
};

export const getElasticCloudApiKey = (
  organizationId: string,
  region: string
) => {
  return instance.post<GetElasticCloudApiKeyResponse>(
    `/api/installation-wizard/v1/organization/${organizationId}/create-api-key?region=${region}`,
    {},
    {
      headers: {
        organizationId,
        // Adding header to avoid showing a toast error
        skipAutoOpsError: 'true',
        // Adding header to avoid redirection to Cloud
        skipAutoOpsRedirect: 'true',
      },
    }
  );
};

export const getProjectsList = ({ from, to }: { from: number; to: number }) => {
  return instance.post<Project[]>(`/api/accounts/projects`, {
    timeRange: { from, to },
  });
};

export const getProjectMetrics = ({
  level,
  projectId,
  to,
  metricTypes,
  from,
  allowedIndices,
  aggTypeToStatsMap,
}: MetricsPayload) => {
  return instance.post<MetricsResponse>(
    `/api/monitoring/serverless/v1/projects/${projectId}/metrics`,
    {
      from,
      to,
      metric_types: metricTypes,
      allowed_indices: allowedIndices ?? [],
      size: 100,
      level,
      aggTypeToStatsMap,
    }
  );
};

export const getIndexList = ({
  projectId,
  level,
  sort,
  sortOrder,
  from,
  to,
}: {
  projectId: string;
  level: Level;
  sort: ChartSort;
  sortOrder: ChartsSortDirection;
  from: number;
  to: number;
}) => {
  return instance.post<MetricsListResponse[]>(
    `/api/monitoring/serverless/v1/projects/${projectId}/metrics-list`,
    {
      size: 100,
      level,
      metric_types: ['ingest_rate', 'storage_retained'],
      allowed_indices: [],
      sort,
      sort_order: sortOrder,
      from,
      to,
    }
  );
};

export const getProjectMetricsList = ({
  projectId,
  type,
  metricTypes,
  sort,
  sortOrder,
  from,
  to,
  signal,
}: PropsWithSignal<{
  projectId: string;
  type: 'indices' | 'data-streams';
  sort: string;
  sortOrder: ChartsSortDirection;
  metricTypes: MetricListTypes;
  from: number;
  to: number;
}>) => {
  return instance.post<GetProjectMetricsListResponse>(
    `/api/monitoring/serverless/v1/projects/${projectId}/${type}/metrics`,
    {
      size: 100,
      metric_types: metricTypes,
      allowed_indices: [],
      sort,
      sort_order: sortOrder,
      from,
      to,
    },
    { signal }
  );
};

export const getSearchTierSettings = ({
  projectId,
  from,
  to,
  metricTypes,
  level,
}: {
  projectId: string;
  from: number;
  to: number;
  metricTypes: SearchTierMetricType[];
  level: Level;
}) => {
  return instance.post<SearchTierSettingsResponse>(
    `/api/monitoring/serverless/v1/projects/${projectId}/search-tier-settings`,
    {
      from,
      to,
      metric_types: metricTypes,
      level,
    }
  );
};

export const getDeploymentsList = ({ signal }: PropsWithSignal) => {
  return instance.get<Deployment[]>(`/api/accounts/deployments`, { signal });
};

export const getDeploymentInfo = ({
  deploymentId,
  from,
  to,
  signal,
}: PropsWithSignal<{
  deploymentId: string;
  from: number;
  to: number;
}>) => {
  return instance.get<DeploymentInfoResponse>(
    `/api/monitoring/deployments/${deploymentId}/deployment-info/v2?from=${from}&to=${to}`,
    { signal, headers: { skipAutoOpsError: 'true' } }
  );
};

export const getDismissedEvents = (resourceType: ResourceType) => {
  return instance.get<DismissEventSetting[]>(
    `/api/analyzer/dismiss-able?resource-type=${resourceType}`
  );
};

export const setDismissedEvents = (events: DismissEvent[]) => {
  return instance.post<string[]>('/api/analyzer/dismiss', events);
};

export const getAnalysesTypes = () => {
  return instance.get<Event[]>('/api/analyzer/analyses-types');
};

export const getFilters = (resourceType: ResourceType) => {
  return instance.get<ListFilter[]>(
    `/api/notifier/filters?resource-types=${resourceType}`
  );
};

export const addFilter = (filter: Partial<AddEditFilter>) => {
  return instance.post<ListFilter>('/api/notifier/filters', filter);
};

export const editFilter = (
  filterId: number,
  filter: Partial<AddEditFilter>
) => {
  return instance.put<ListFilter>(`/api/notifier/filters/${filterId}`, filter);
};

export const deleteFilter = (filterId: number) => {
  return instance.delete<void>(`/api/notifier/filters/${filterId}`);
};

export const getConnectors = (resourceType: ResourceType) => {
  return instance.get<Connector[]>(
    `/api/notifier/endpoint?resource-types=${resourceType}`
  );
};

export const getConnectorsForEvent = ({
  deploymentId,
  analysisType,
  severity,
  signal,
}: PropsWithSignal<{
  deploymentId: string;
  analysisType: string;
  severity: EventSeverity;
}>) => {
  return instance.post<ConnectorForEvent[]>(
    `/api/notifier/endpoint/analysis`,
    { deploymentId, analysisType, severity },
    { signal, headers: { skipAutoOpsError: 'true' } }
  );
};

export const addConnector = (
  connector: Partial<Connector>,
  resourceType: ResourceType
) => {
  return instance.post<Connector>('/api/notifier/endpoint', {
    ...connector,
    resourceType,
  });
};

export const editConnector = (
  connectorId: number,
  connector: Partial<Connector>,
  resourceType: ResourceType
) => {
  return instance.put<Connector>(`/api/notifier/endpoint/${connectorId}`, {
    ...connector,
    resourceType,
  });
};

export const deleteConnector = (connectorId: number) => {
  return instance.delete<void>(`/api/notifier/endpoint/${connectorId}`);
};

export const testConnector = (connectorSettings: ConnectorSettings) => {
  return instance.post<void>(`/api/notifier/endpoint/test`, connectorSettings, {
    headers: { skipAutoOpsError: 'true' },
  });
};

export const getIndices = ({
  deploymentId,
  pagination,
  size,
  from,
  to,
  sort,
  sortOrder,
  search,
  offset,
  signal,
}: PropsWithSignal<GetIndicesPayload>) => {
  const url = `/api/monitoring/deployments/${deploymentId}/indices?pagination=${pagination}&size=${size}&from=${from}&to=${to}&sort=${sort}&sortOrder=${sortOrder}`;
  const withSearch = search ? `${url}&search=${search}` : url;
  const withOffset = offset ? `${withSearch}&offset=${offset}` : withSearch;

  return instance.get<GetIndicesResponse>(withOffset, { signal });
};

export const getIndicesNames = ({
  deploymentId,
  from,
  to,
  signal,
}: PropsWithSignal<{
  deploymentId: string;
  from: number;
  to: number;
}>) => {
  return instance.get<GetIndicesResponse>(
    `/api/monitoring/deployments/${deploymentId}/indices?include_stats=false&from=${from}&to=${to}`,
    { signal }
  );
};

export const getIndexMetrics = ({
  deploymentId,
  indexName,
  data,
}: GetIndexMetricsPayload) => {
  return instance.post<GetIndexMetricsResponse>(
    `/api/monitoring/metrics/deployments/${deploymentId}/${indexName}`,
    data
  );
};

export const getNotificationsSummary = ({
  deploymentIds,
  eventTypes = [],
  notificationStatuses = [],
  endpointIds = [],
  from,
  to,
  sortBy,
}: GetNotificationsSummaryPayload) => {
  return instance.post<NotificationsHistorySummaryResponse>(
    `/api/notifier/notification-view/report`,
    {
      deploymentIds,
      eventTypes,
      notificationStatuses,
      endpointIds,
      timeRange: { from, to },
      sortBy,
    }
  );
};

export const getNotificationsHistory = ({
  deploymentIds,
  eventTypes = [],
  notificationStatuses = [],
  endpointIds = [],
  from,
  to,
  size,
  offset,
  sortBy,
  sortOrder,
  signal,
}: PropsWithSignal<GetNotificationsHistoryPayload>) => {
  return instance.post<NotificationsHistoryTableResponse>(
    `/api/notifier/notification-view/history`,
    {
      deploymentIds,
      eventTypes,
      notificationStatuses,
      endpointIds,
      timeRange: { from, to },
      size,
      offset,
      sortBy,
      sortOrder,
    },
    { signal }
  );
};

export const getTemplatesHistory = ({
  deploymentId,
  signal,
}: PropsWithSignal<{ deploymentId: string }>) => {
  return instance.get<TemplatesHistoryResponse>(
    `/api/analyzer/tools/templates/history/${deploymentId}`,
    { signal }
  );
};

export const getTemplatesResult = ({
  templateId,
  signal,
}: PropsWithSignal<{ templateId: string }>) => {
  return instance.get<TemplateResultResponse>(
    `/api/analyzer/tools/templates/result/${templateId}`,
    {
      headers: {
        // Adding header to avoid showing a toast error
        skipAutoOpsError: 'true',
      },
      signal,
    }
  );
};

export const getCustomizedEvents = (resourceType: ResourceType) => {
  return instance.get<CustomizedEvent[]>(
    `/api/analyzer/customize?resource-type=${resourceType}`
  );
};

export const resetCustomizedEvents = ({
  eventType,
  deploymentIds,
}: {
  eventType: string;
  deploymentIds: string[];
}) => {
  return instance.post<void>(
    `/api/analyzer/customize/reset/analysis/${eventType}`,
    {
      deploymentIds: deploymentIds,
    }
  );
};

export const getCustomizedEventDefaultValues = ({
  eventType,
  resourceType,
}: {
  eventType: string;
  resourceType: ResourceType;
}) => {
  return instance.get<CustomizedEventData>(
    `/api/analyzer/customize/default/analysis/${eventType}?resource-type=${resourceType}`,
    { headers: { skipAutoOpsError: 'true' } }
  );
};

export const createCustomizedEvent = (
  eventType: string,
  body: CustomizedEventBody
) => {
  return instance.post<void>(`/api/analyzer/customize/${eventType}`, body);
};

export const getCustomizedEventByDeployment = ({
  deploymentId,
  eventType,
}: {
  deploymentId: string;
  eventType: string;
}) => {
  return instance.get<CustomizedEditedEventData>(
    `/api/analyzer/customize/deployments/${deploymentId}/analysis/${eventType}`
  );
};

export const getEventsOverTime = ({
  pathParams: { deploymentId },
  payload: { from, to, bucketSize, severity, eventTypes },
  signal,
}: PropsWithSignal<{
  pathParams: GetEventsOverTimePathParams;
  payload: GetEventsOverTimePayload;
}>) => {
  return instance.post<GetEventsOverTimeResponse>(
    `/api/analyzer/deployments/${deploymentId}/v2/events-over-time`,
    { from, to, bucketSize, severity, eventTypes },
    { signal, headers: { skipAutoOpsError: 'true' } }
  );
};

export const getDeploymentAnalysis = ({
  deploymentId,
  analysisId,
  signal,
}: PropsWithSignal<{
  deploymentId: string;
  analysisId: number;
}>) => {
  return instance.get<DeploymentAnalysisResponse>(
    `/api/analyzer/deployments/${deploymentId}/analysis/${analysisId}`,
    { signal }
  );
};

export const getOpenEventGroups = ({
  pathParams: { deploymentId },
  payload: { from, to, eventTypes },
  signal,
}: PropsWithSignal<{
  pathParams: GetOpenEventGroupsPathParams;
  payload: GetOpenEventGroupsPayload;
}>) => {
  return instance.post<GetOpenEventGroupsResponse>(
    `/api/analyzer/deployments/${deploymentId}/open-event-groups`,
    { from, to, eventTypes },
    { signal, headers: { skipAutoOpsError: 'true' } }
  );
};

export const getClosedEventGroups = ({
  pathParams: { deploymentId },
  payload: { from, to, eventTypes },
  signal,
}: PropsWithSignal<{
  pathParams: GetClosedEventGroupsPathParams;
  payload: GetClosedEventGroupsPayload;
}>) => {
  return instance.post<GetClosedEventGroupsResponse>(
    `/api/analyzer/deployments/${deploymentId}/closed-event-groups`,
    { from, to, eventTypes },
    { signal, headers: { skipAutoOpsError: 'true' } }
  );
};

export const getOpenEvents = ({
  pathParams: { deploymentId },
  payload: { type, from, to, size, offset },
  signal,
}: PropsWithSignal<{
  pathParams: GetOpenEventsPathParams;
  payload: GetOpenEventsPayload;
}>) => {
  return instance.post<GetOpenEventsResponse>(
    `/api/analyzer/deployments/${deploymentId}/open-events`,
    { nodeIds: null, type, from, to, size, offset },
    { signal, headers: { skipAutoOpsError: 'true' } }
  );
};

export const getClosedEvents = ({
  pathParams: { deploymentId },
  payload: { type, from, to, size, offset },
  signal,
}: PropsWithSignal<{
  pathParams: GetClosedEventsPathParams;
  payload: GetClosedEventsPayload;
}>) => {
  return instance.post<GetClosedEventsResponse>(
    `/api/analyzer/deployments/${deploymentId}/closed-events`,
    { type, from, to, size, offset },
    { signal, headers: { skipAutoOpsError: 'true' } }
  );
};

export const getNodesList = ({
  deploymentId,
  from,
  to,
  signal,
}: PropsWithSignal<{
  deploymentId: string;
  from: number;
  to: number;
}>) => {
  return instance.get<GetNodesResponse>(
    `/api/monitoring/deployments/${deploymentId}/nodes?from=${from}&to=${to}`,
    { signal, headers: { skipAutoOpsError: 'true' } }
  );
};

export const getNodesMetrics = ({
  deploymentId,
  nodeIds,
  aggTypeToStatsMap,
  from,
  to,
  buckets = 20,
  signal,
}: PropsWithSignal<GetNodesMetricsPayload>) => {
  return instance.post<GetNodesMetricsResponse>(
    `/api/monitoring/metrics/deployments/${deploymentId}/v2`,
    {
      nodeIds,
      timeRange: { from, to },
      aggTypeToStatsMap,
      buckets,
    },
    { signal, headers: { skipAutoOpsError: 'true' } }
  );
};

export const getDeploymentTierPerformance = ({
  deploymentId,
  tiers,
  from,
  to,
  signal,
}: PropsWithSignal<{
  deploymentId: string;
  tiers: DeploymentTierPerformanceTier[];
  from: number;
  to: number;
}>) => {
  return instance.post<DeploymentTierPerformanceResponse>(
    `/api/monitoring/deployment-tier-performance/metrics/deployments/${deploymentId}`,
    { tiers, timeRange: { from, to }, buckets: 20 },
    { signal, headers: { skipAutoOpsError: 'true' } }
  );
};

export const getDashboardData = ({
  from,
  to,
  resourceTypes,
}: {
  from: number;
  to: number;
  resourceTypes: string[];
}) => {
  return instance.post<DashboardDataResponse>('/api/analyzer/dashboard', {
    timeRange: { from, to },
    resourceTypes,
  });
};

export const getPermissions = ({ signal }: PropsWithSignal) => {
  return instance.get<PermissionsResponse>('/api/users/v2/permissions', {
    signal,
    headers: { skipAutoOpsError: 'true' },
  });
};

export const getIndexNodesRates = ({
  deploymentId,
  data,
  signal,
}: PropsWithSignal<{
  deploymentId: string;
  data: ShardsViewBody;
}>) => {
  return instance.post<IndexNodesRatesResponse>(
    `/api/monitoring/deployments/${deploymentId}/index-nodes-rates`,
    data,
    { signal, headers: { skipAutoOpsError: 'true' } }
  );
};

export const getIndexOnNodeData = ({
  deploymentId,
  data,
  signal,
}: PropsWithSignal<{
  deploymentId: string;
  data: IndexOnNodeDataBody;
}>) => {
  return instance.post<IndexOnNodeDataResponse>(
    `/api/monitoring/deployments/${deploymentId}/index-node-data`,
    data,
    { signal, headers: { skipAutoOpsError: 'true' } }
  );
};

export const getDashboardEvents = ({
  severityList,
  eventTypes,
  deploymentIds,
  from,
  to,
  signal,
}: PropsWithSignal<{
  severityList: string[];
  eventTypes: string[];
  deploymentIds: string[];
  from: number;
  to: number;
}>) => {
  return instance.post<DashboardEventsResponse>(
    '/api/analyzer/dashboard/events',
    {
      severityList,
      eventTypes,
      deploymentIds,
      numberOfCards: 10,
      timeRange: { from, to },
    },
    { signal }
  );
};
